import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Send, Bookmark, Music2 } from 'lucide-react';
import { Avatar } from '@/shared/ui/Avatar';
import { useStore } from '@/shared/state/store';
import { getUserById } from '@/shared/data';
import type { Reel } from '@/shared/types';
import { formatCount } from '@/shared/utils/format';
import { cn } from '@/shared/utils/cn';

type Props = {
  reel: Reel;
  onOpenComments: () => void;
};

export function ReelOverlay({ reel, onOpenComments }: Props) {
  const author = getUserById(reel.authorId);
  const liked = useStore((s) => s.likedReelIds.has(reel.id));
  const saved = useStore((s) => s.savedPostIds.has(reel.id));
  const toggleLike = useStore((s) => s.toggleReelLike);
  const toggleSave = useStore((s) => s.togglePostSave);
  if (!author) return null;

  return (
    <>
      <div className="absolute right-3 bottom-28 flex flex-col items-center gap-5 z-20">
        <Link to={`/instaflix/profile/${author.username}`} aria-label={`@${author.username} profile`}>
          <Avatar src={author.avatar} alt={author.username} size={44} ring="violet" />
        </Link>
        <button
          data-testid="reel-like"
          data-liked={liked}
          onClick={() => toggleLike(reel.id)}
          aria-label="Like"
          className="flex flex-col items-center gap-1 text-white active:scale-90 transition-transform"
        >
          <Heart className={cn('w-7 h-7', liked ? 'fill-rose-500 text-rose-500' : '')} />
          <span className="text-[10px]">{formatCount(reel.likesCount + (liked ? 1 : 0))}</span>
        </button>
        <button
          data-testid="reel-comment-button"
          onClick={onOpenComments}
          aria-label="Comments"
          className="flex flex-col items-center gap-1 text-white active:scale-90 transition-transform"
        >
          <MessageCircle className="w-7 h-7" />
          <span className="text-[10px]">{formatCount(reel.commentsCount)}</span>
        </button>
        <button
          aria-label="Share"
          className="flex flex-col items-center gap-1 text-white active:scale-90 transition-transform"
        >
          <Send className="w-7 h-7" />
          <span className="text-[10px]">{formatCount(reel.sharesCount)}</span>
        </button>
        <button
          onClick={() => toggleSave(reel.id)}
          aria-label="Save"
          className="flex flex-col items-center gap-1 text-white active:scale-90 transition-transform"
        >
          <Bookmark className={cn('w-7 h-7', saved ? 'fill-amber-200 text-amber-200' : '')} />
        </button>
      </div>

      <div className="absolute left-4 right-20 bottom-24 z-20">
        <Link to={`/instaflix/profile/${author.username}`} className="text-white font-medium text-sm">
          @{author.username}
        </Link>
        <p className="text-white/80 text-sm mt-1 leading-snug line-clamp-2">{reel.caption}</p>
        <div className="mt-2 flex items-center gap-1.5 text-white/60 text-[11px]">
          <Music2 className="w-3 h-3" /> {reel.audioTitle}
        </div>
      </div>
    </>
  );
}
